import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';

/** easeOutCubic — desaceleração suave, alinhada ao "reveal de dados" do DESIGN.md. */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Anima um número inteiro (centavos) 0→alvo — ou do valor corrente→alvo ao mudar —
 * em ~700ms (exceção de "reveal de dados" do DESIGN.md). Respeita
 * prefers-reduced-motion indo direto ao alvo. Retorna o valor corrente em
 * centavos para o chamador formatar com `formatBRL`.
 *
 * Usa requestAnimationFrame puro (sem MotionValue) — garante re-render a cada
 * frame, independente da versão do motion. `delay` e `duration` em SEGUNDOS.
 */
export function useCountUp(target: number, delay = 0, duration = 0.7): number {
  const reduce = useReducedMotion();
  const [current, setCurrent] = useState(0);
  const fromRef = useRef(0);

  useEffect(() => {
    // Movimento desligado (preferência do usuário) ou aba oculta (rAF fica
    // pausado em background): vai direto ao alvo, sem depender de frames.
    if (reduce || document.hidden) {
      fromRef.current = target;
      setCurrent(target);
      return;
    }
    const from = fromRef.current;
    const durMs = duration * 1000;
    let raf = 0;
    let start = 0;
    const tick = (ts: number): void => {
      if (!start) start = ts;
      const t = durMs <= 0 ? 1 : Math.min(1, (ts - start) / durMs);
      const v = from + (target - from) * easeOut(t);
      fromRef.current = v;
      setCurrent(Math.round(v));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    const startTimer = window.setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, delay * 1000);
    // Rede de segurança: garante o valor final mesmo se o rAF for estrangulado
    // (aba em segundo plano) — timers seguem rodando; o valor nunca fica preso.
    const safety = window.setTimeout(
      () => {
        fromRef.current = target;
        setCurrent(target);
      },
      delay * 1000 + durMs + 250,
    );
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(safety);
      cancelAnimationFrame(raf);
    };
  }, [target, delay, duration, reduce]);

  return current;
}
